import React, { useMemo } from 'react'
import { useParams } from 'react-router-dom'

import { useFetchList } from 'store'
import {
	Appointment,
	AppointmentProtocolRule,
	ClinicalProcess,
	Disability,
	MedicalActProtocolRule,
	MedicalSpecialtyDisability,
	Requisition,
	Task,
	TrustNotification,
} from 'models'

import useSession from 'hooks/useSession'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'

import Widget from '@jimmyboys/liita-ui/dist/components/Widget'
import * as widgets from '../../components/widgets/index'

import styles from './ContextualController.module.sass'
import Note from 'models/Note'
import MedicalOpinion from 'models/MedicalOpinion'
import TransportInfo from 'models/TransportInfo'

const ContextualClinicalProcessController = () => {
	const { i18n } = useGlobal()
	const params = useParams()
	const { user: sessionUser } = useSession()
	const processUri = `/clinical_processes/${params.clinicalProcessId}`
	const requisitionsUri = `/clinical_processes/${params.clinicalProcessId}/requisitions`
	const appointmentsUri = `/clinical_processes/${params.clinicalProcessId}/appointments`
	const tasksUri = `/clinical_processes/${params.clinicalProcessId}/tasks`
	const notificationsUri = `/clinical_processes/${params.clinicalProcessId}/notifications`
	const disabilityHistoryUri = `/clinical_processes/${params.clinicalProcessId}/disability_history`
	const processNotesUri = `/notes/clinical_process/${params.clinicalProcessId}`
	const managementOpinionUri = `clinical_processes/${params.clinicalProcessId}/management_opinions`
	const disabilitiesUri = `/clinical_processes/${params.clinicalProcessId}/disabilities`
	const disabilitiesNewEngineUri = `/clinical_processes/${params.clinicalProcessId}/disabilities-new-engine`
	const latestTriageUri = `/clinical_processes/${params.clinicalProcessId}/latest_triage`
	const appointmentProcessProtocolsUri = `/rules/clinical_process/${params.clinicalProcessId}?ruleType=AutomationRule&taskType=AppointmentClinicalApproval`
	const medicalActProcessProtocolsUri = `/rules/clinical_process/${params.clinicalProcessId}?ruleType=AutomationRule&taskType=MedicalActsClinicalApproval`
	const providerEntitiesUri = '/metadata/provider_entities'
	const medicalSpecialtiesUri = '/metadata/medical_specialties'
	const medicalActsUri = '/metadata/medical_acts'
	const transportsUri = `/clinical_processes/${params.clinicalProcessId}/transport`

	const { contents } = useFetchList(
		[
			processUri,
			requisitionsUri,
			appointmentsUri,
			tasksUri,
			notificationsUri,
			disabilityHistoryUri,
			processNotesUri,
			managementOpinionUri,
			disabilitiesUri,
			latestTriageUri,
			transportsUri,
			providerEntitiesUri,
			medicalSpecialtiesUri,
			medicalActsUri,
			appointmentProcessProtocolsUri,
			medicalActProcessProtocolsUri,
			disabilitiesNewEngineUri,
		],
		[
			ClinicalProcess.fromJSON,
			Requisition.fromJSON,
			Appointment.fromJSON,
			Task.fromJSON,
			TrustNotification.fromJSON,
			MedicalSpecialtyDisability.fromJSON,
			Note.fromJSON,
			MedicalOpinion.fromJSON,
			Disability.fromJSON,
			MedicalOpinion.fromJSON,
			TransportInfo.fromJSON,
		]
	)

	const [
		process,
		requisitions,
		appointments,
		tasks,
		notifications,
		disabilityHistory,
		notes,
		managementOpinions,
		disabilities,
		latestTriage,
		transports,
		providerEntitiesList,
		medicalSpecialtiesList,
		medicalActsList,
		appointmentProcessProtocolRules,
		medicalActProcessProtocolRules,
		disabilitiesNewEngine,
	] = contents

	const appointmentProcessProtocols =
		AppointmentProtocolRule.retrieveRuleValues(
			appointmentProcessProtocolRules
		)
	const medicalActProcessProtocols =
		MedicalActProtocolRule.retrieveRuleValues(
			medicalActProcessProtocolRules
		)

	const notificationsForProcess = (notifications ?? []).filter(
		(item) => item.processId === params.clinicalProcessId
	)

	const providerEntities = providerEntitiesList?.reduce(
		(acc, { id, name }) => {
			acc[id] = name

			return acc
		},
		{}
	)

	const medicalSpecialties = medicalSpecialtiesList?.reduce(
		(acc, { id, name }) => {
			acc[id] = name

			return acc
		},
		{}
	)

	const medicalActs = medicalActsList?.reduce((acc, { id, name }) => {
		acc[id] = name

		return acc
	}, {})

	const isAT = useMemo(() => {
		if (process?.accident?.segment?.abbreviation) {
			return process?.accident?.segment?.abbreviation === 'AT'
		}

		return true
	}, [process?.accident?.segment?.abbreviation])

	const userRoles = sessionUser?.roles || []
	const widgetsByRole = {
		doctor: [
			'TasksWidget',
			'NotesWidget',
			!isAT && 'ProcessCostsWidget',
			'ProcessProtocolsWidget',
			'NotificationsWidget',
			'TransportsWidget',
			'InsurancePolicyWidget',
			'BodyPartInjuryTypeWidget',
			'DisabilitiesWidget',
			'DisabilitiesNewEngineWidget',
			'SmsWidget',
			'TrustClaimsManagerWidget',
			'CompanyClaimsManagerWidget',
			'InjuredWidget',
		],
		support: [
			'TasksWidget',
			'NotesWidget',
			!isAT && 'ProcessCostsWidget',
			'ProcessProtocolsWidget',
			'ManagementOpinionWidget',
			'NotificationsWidget',
			'TransportsWidget',
			'InsurancePolicyWidget',
			'SmsWidget',
			'DisabilitiesWidget',
			'DisabilitiesNewEngineWidget',
			'BodyPartInjuryTypeWidget',
			'InjuredWidget',
			'TrustClaimsManagerWidget',
			'CompanyClaimsManagerWidget',
		],
	}

	const role =
		userRoles.includes('support') ||
		userRoles.includes('manager') ||
		userRoles.includes('admin')
			? 'support'
			: 'doctor'

	return (
		<>
			<Widget
				className={styles['welcome-widget']}
				paddings={['small', '', '']}
			>
				<h2
					className={styles['welcome-title']}
				>{i18n`Process Info`}</h2>
				{(widgetsByRole[role] || []).map((widget, index) => {
					if (!widget) return null

					const Widget = widgets[widget]

					return (
						<Widget
							key={index}
							data={{
								process,
								requisitions,
								appointments,
								tasks,
								notifications: notificationsForProcess,
								disabilityHistory,
								notes,
								managementOpinions,
								disabilities,
								disabilitiesNewEngine,
								latestTriage,
								appointmentProcessProtocols,
								medicalActProcessProtocols,
								providerEntities,
								medicalSpecialties,
								medicalActs,
								transports,
							}}
						/>
					)
				})}
			</Widget>
		</>
	)
}

export default ContextualClinicalProcessController
